import { useRef, useState } from "react";
import { UploadCloud, FileArchive, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function UploadZone({ onUploaded }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  const upload = async (f) => {
    if (!f) return;
    if (!/\.(mdb|accdb|wdb)$/i.test(f.name)) {
      toast.error("Please choose a .mdb, .accdb or .wdb file");
      return;
    }
    setFile(f);
    setBusy(true);
    setProgress(0);
    const form = new FormData();
    form.append("file", f);
    try {
      const { data } = await api.post("/databases/upload", form, {
        onUploadProgress: (e) => e.total && setProgress(Math.round((e.loaded / e.total) * 100)),
      });
      const failed = data.tables.filter((t) => t.error).length;
      const ok = data.tables.length - failed;
      toast.success(`Converted ${ok} table${ok === 1 ? "" : "s"} from ${data.filename}`);
      if (failed) toast.warning(`${failed} table(s) could not be read`);
      onUploaded(data);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
      setFile(null);
    }
  };

  return (
    <div className="max-w-3xl fade-up">
      <p className="text-xs font-mono uppercase tracking-widest text-cyan-400 mb-3">New conversion</p>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Upload an Access or Works database</h1>
      <p className="mt-3 text-muted-foreground">We read every table in your Access (.mdb, .accdb) or Works (.wdb) file and turn it into an editable web view.</p>

      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files[0]); }}
        onClick={() => !busy && inputRef.current?.click()}
        className={`mt-8 relative overflow-hidden cursor-pointer rounded-lg border-2 border-dashed p-12 text-center transition-colors grid-bg
          ${drag ? "border-primary bg-primary/5" : "border-border hover:border-primary/60"}`}>
        <input ref={inputRef} type="file" accept=".mdb,.accdb,.wdb" className="hidden" data-testid="upload-file-input"
          onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} />
        {busy ? (
          <div className="space-y-4" data-testid="upload-progress">
            <Loader2 className="mx-auto animate-spin text-primary" size={36} />
            <div className="inline-flex items-center gap-2 rounded-full bg-secondary px-3 py-1 text-xs font-mono">
              <FileArchive size={12} /> {file?.name} · {(file?.size / 1024 / 1024).toFixed(2)} MB
            </div>
            <div className="mx-auto max-w-xs h-1.5 rounded bg-secondary overflow-hidden relative">
              {progress < 100 ? <div className="h-full bg-primary transition-[width]" style={{ width: `${progress}%` }} />
                : <div className="absolute inset-y-0 w-1/3 bg-cyan-400 scan-line" />}
            </div>
            <p className="text-sm text-muted-foreground">{progress < 100 ? `Uploading ${progress}%` : "Converting tables…"}</p>
          </div>
        ) : (
          <>
            <UploadCloud className="mx-auto text-primary" size={40} />
            <p className="mt-4 font-medium">Drag & drop your file here</p>
            <p className="text-sm text-muted-foreground mt-1">.mdb, .accdb or Microsoft Works .wdb</p>
            <Button className="mt-6 hover:bg-blue-600 hover:text-white" data-testid="upload-browse-button" type="button">Choose file</Button>
          </>
        )}
      </div>

      <div className="mt-10 grid sm:grid-cols-3 gap-4 text-sm">
        {[["View & search", "Every table becomes a fast, searchable grid."], ["Edit records", "Add, change or delete rows right in the browser."], ["Export anytime", "Download as CSV, Excel, JSON or Access."]].map(([t, d], i) => (
          <div key={t} className="border rounded-lg p-4 bg-card fade-up" style={{ animationDelay: `${100 + i * 80}ms` }}>
            <p className="font-display font-bold">{t}</p>
            <p className="text-muted-foreground mt-1">{d}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
